import { useTranslation } from "react-i18next";
import {
  Building2,
  Package2,
  ReceiptText,
  Rocket,
  X,
} from "lucide-react";

export interface WelcomeOnboardingModalProps {
  isOpen: boolean;
  organizationName: string;
  displayName: string;
  onClose: () => void;
  onDontShowAgain: () => void;
  onGoSettings: () => void;
  onGoInventory: () => void;
  onGoSales: () => void;
}

export function WelcomeOnboardingModal({
  isOpen,
  organizationName,
  displayName,
  onClose,
  onDontShowAgain,
  onGoSettings,
  onGoInventory,
  onGoSales,
}: WelcomeOnboardingModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-modal-title"
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-godo-navy via-godo-navy to-[#0056a7] px-6 py-7 text-white sm:px-8">
          <button
            type="button"
            aria-label={t("labels.close_menu")}
            onClick={onClose}
            className="absolute top-4 ltr:right-4 rtl:left-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex items-center gap-3">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
              <Rocket className="h-7 w-7" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                {t("welcome.onboarding_badge")}
              </p>
              <h2
                id="welcome-modal-title"
                className="mt-1 truncate text-2xl font-bold sm:text-3xl"
              >
                {t("welcome.title", { name: displayName })}
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-xl text-sm leading-7 text-white/85">
            {t("welcome.subtitle", {
              company: organizationName,
              app: t("labels.app_name"),
            })}
          </p>
        </div>

        <div className="space-y-3 px-6 py-6 sm:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-400">
            {t("welcome.quick_start_heading")}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={onGoSettings}
              className="group flex flex-col items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-right transition hover:-translate-y-0.5 hover:border-godo-accent/60 hover:bg-godo-accent/5 hover:shadow-md"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 transition group-hover:bg-amber-500 group-hover:text-white group-hover:ring-amber-500/60">
                <Building2 className="h-5 w-5" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {t("welcome.action_settings_title")}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">
                  {t("welcome.action_settings_desc")}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={onGoInventory}
              className="group flex flex-col items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-right transition hover:-translate-y-0.5 hover:border-emerald-500/60 hover:bg-emerald-50 hover:shadow-md"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 transition group-hover:bg-emerald-500 group-hover:text-white group-hover:ring-emerald-500/60">
                <Package2 className="h-5 w-5" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {t("welcome.action_inventory_title")}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">
                  {t("welcome.action_inventory_desc")}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={onGoSales}
              className="group flex flex-col items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-right transition hover:-translate-y-0.5 hover:border-sky-500/60 hover:bg-sky-50 hover:shadow-md"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700 ring-1 ring-sky-200 transition group-hover:bg-sky-500 group-hover:text-white group-hover:ring-sky-500/60">
                <ReceiptText className="h-5 w-5" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {t("welcome.action_sales_title")}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">
                  {t("welcome.action_sales_desc")}
                </p>
              </div>
            </button>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <button
            type="button"
            onClick={onDontShowAgain}
            className="rounded-lg px-3 py-2 text-xs font-medium text-slate-500 transition hover:bg-white hover:text-slate-700 hover:shadow-sm"
          >
            {t("welcome.dont_show_again")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-godo-navy px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#003d8f] focus:outline-none focus:ring-2 focus:ring-godo-navy/40"
          >
            {t("welcome.explore_dashboard")}
          </button>
        </div>
      </div>
    </div>
  );
}
